import React, { useState } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { OwnershipSummary } from './OwnershipSummary';
import { OwnershipTable } from './OwnershipTable';
import { OwnershipModal } from './OwnershipModal';
import { LoanSelectionModal } from './LoanSelectionModal';
import { useOwnershipData } from '../hooks/useOwnershipData';
import { CURRENT_ENTITY } from '../data/mockData';
import { Plus, History, Building2, AlertTriangle, Save } from 'lucide-react';
import { OwnershipRecord } from '../types/ownership';
export function OwnershipDashboard() {
  const {
    records,
    pendingChanges,
    hasPendingChanges,
    totalOwnership,
    primaryMemberCount,
    showHistorical,
    setShowHistorical,
    addRecord,
    updateRecord,
    deleteRecord,
    applyPendingChanges,
    discardPendingChanges
  } = useOwnershipData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoanModalOpen, setIsLoanModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<OwnershipRecord | undefined>(undefined);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const handleAddNew = () => {
    setEditingRecord(undefined);
    setIsModalOpen(true);
  };
  const handleEdit = (record: OwnershipRecord) => {
    setEditingRecord(record);
    setIsModalOpen(true);
  };
  const handleDelete = (id: string) => {
    const isPendingAdd = pendingChanges.some(c => c.type === 'add' && c.record.id === id);
    if (isPendingAdd || window.confirm('Are you sure you want to delete this future-dated record?')) {
      const result = deleteRecord(id);
      if (result && !result.success) {
        setDeleteError(result.error || 'Failed to delete');
        setTimeout(() => setDeleteError(null), 3000);
      }
    }
  };
  const handleSave = (recordData: Omit<OwnershipRecord, 'id' | 'createdDate'>) => {
    if (editingRecord) {
      return updateRecord(editingRecord.id, recordData);
    } else {
      return addRecord(recordData);
    }
  };
  const handleApplyChanges = (selectedLoanIds: string[]) => {
    console.log('Applying changes to loans:', selectedLoanIds);
    applyPendingChanges();
    setIsLoanModalOpen(false);
  };
  // Build pending changes summary text
  const getPendingChangesSummary = () => {
    const adds = pendingChanges.filter(c => c.type === 'add').length;
    const edits = pendingChanges.filter(c => c.type === 'edit').length;
    const deletes = pendingChanges.filter(c => c.type === 'delete').length;
    const parts = [];
    if (adds > 0) parts.push(`${adds} added`);
    if (edits > 0) parts.push(`${edits} edited`);
    if (deletes > 0) parts.push(`${deletes} deleted`);
    return parts.join(', ');
  };
  // Card actions with pending changes UI
  const cardActions = hasPendingChanges ? <div className="flex items-center gap-3" data-id="element-45">
      <div className="flex items-center gap-2 px-3 py-1.5 bg-[#fff8c5] border border-[#d4a72c] rounded-[4px]" data-id="element-46">
        <AlertTriangle className="w-4 h-4 text-[#8e5b04]" data-id="element-47" />
        <span className="text-xs font-medium text-[#3e3e3c]" data-id="element-48">
          {pendingChanges.length} unsaved: {getPendingChangesSummary()}
        </span>
      </div>
      <Button variant="neutral" size="sm" onClick={discardPendingChanges} data-id="element-49">
        Discard
      </Button>
      <Button variant="primary" size="sm" onClick={() => setIsLoanModalOpen(true)} leftIcon={<Save className="w-4 h-4" data-id="element-51" />} data-id="element-50">
        Save Changes
      </Button>
    </div> : null;
  return <div className="relative" data-id="element-52">
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-id="element-53">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-54">
          <div className="flex items-center gap-3" data-id="element-55">
            <div className="p-3 bg-white border border-[#dddbda] rounded-[4px] shadow-sm" data-id="element-56">
              <Building2 className="w-6 h-6 text-[#0176d3]" data-id="element-57" />
            </div>
            <div data-id="element-58">
              <h1 className="text-xl font-bold text-[#080707]" data-id="element-59">
                {CURRENT_ENTITY.name}
              </h1>
              <p className="text-sm text-[#706e6b]" data-id="element-60">
                Entity Ownership Management
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-61">
            <Button variant={showHistorical ? 'primary' : 'neutral'} onClick={() => setShowHistorical(!showHistorical)} leftIcon={<History className="w-4 h-4" data-id="element-63" />} data-id="element-62">
              {showHistorical ? 'Hide History' : 'Show History'}
            </Button>
            <Button variant="primary" onClick={handleAddNew} leftIcon={<Plus className="w-4 h-4" data-id="element-65" />} data-id="element-64">
              Add Ownership
            </Button>
          </div>
        </div>

        {deleteError && <div className="bg-[#ba0517] text-white px-4 py-3 rounded-[4px] text-sm font-medium" data-id="element-66">
            {deleteError}
          </div>}

        {/* Summary Cards */}
        <OwnershipSummary totalPercentage={totalOwnership} primaryMemberCount={primaryMemberCount} data-id="element-67" />

        {/* Main Content */}
        <Card title="Ownership Records" actions={cardActions} className="min-h-[400px]" data-id="element-68">
          <OwnershipTable records={records} onEdit={handleEdit} onDelete={handleDelete} isHistoricalView={showHistorical} pendingChanges={pendingChanges} data-id="element-69" />
        </Card>

        {/* Ownership Modal */}
        <OwnershipModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} initialData={editingRecord} entityId={CURRENT_ENTITY.id} data-id="element-70" />

        {/* Loan Selection Modal */}
        <LoanSelectionModal isOpen={isLoanModalOpen} onClose={() => setIsLoanModalOpen(false)} onConfirm={handleApplyChanges} pendingChanges={pendingChanges} data-id="element-71" />
      </div>
    </div>;
}